import Breadcrumbs from "@/components/Breadcrumbs";
import { BimqSynchronisationService, ElementDatabaseService } from "@/services/admin-services";
import { DatabaseElement, SynchronisationStateResponse } from "@/services/admin-services/models";
import { SynchronisationState } from "@/services/admin-services/models/SynchronisationState";
import { LocaleService } from "@/services/global-services";
import { Discipline, Language, Locale } from "@/services/global-services/models";
import { ArrowPathIcon, XMarkIcon } from "@heroicons/react/24/outline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
    Alert,
    AlertColor,
    alpha,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { parseJSON } from "date-fns";
import { ChangeEvent, Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const breadcrumbs = [
    {
        to: "..",
        title: "Administration",
    },
    {
        title: "Content",
        to: "",
    },
    {
        title: "Elements",
        to: "overview",
    },
];

const Overview = () => {
    const theme = useTheme();

    const [loading, setLoading] = useState<boolean>(true);
    const [elements, setElements] = useState<DatabaseElement[]>([]);
    const [disciplines, setDisciplines] = useState<Discipline[]>([]);
    const [languages, setLanguages] = useState<Language[]>([]);

    const [lastSyncDate, setLastSyncDate] = useState<string>("");
    const [syncInProgress, setSyncInProgress] = useState<boolean>(false);
    const [syncNotification, setSyncNotification] = useState<{
        open: boolean;
        message?: string;
        type: AlertColor;
    }>({ open: false, type: "success" });

    // Reflects the backend's last known sync outcome, independent of whether this session/tab is the
    // one that triggered it or is actively polling - so a user who reopens the page later still sees it.
    const [syncError, setSyncError] = useState<{ open: boolean; message?: string }>({ open: false });

    const [openInNewTab, setOpenInNewTab] = useState<boolean>(true);

    const [open, setOpen] = useState<boolean>(false);
    const [ignoreChanges, setIgnoreChanges] = useState<boolean>(true);
    const [ignoreVisibility, setIgnoreVisibility] = useState<boolean>(true);
    const [ignoreFootnotes, setIgnoreFootnotes] = useState<boolean>(true);
    const [selectedFile, setSelectedFile] = useState<File>();

    const popupRef = useRef<Window | null>(null);
    const syncStateTimerRef = useRef<NodeJS.Timeout>();
    const popupTimerRef = useRef<NodeJS.Timeout>();
    const notificationTimerRef = useRef<NodeJS.Timeout>();

    const syncInProgressRef = useRef<boolean>(false);
    syncInProgressRef.current = syncInProgress;
    const syncNotificationRef = useRef<typeof syncNotification>();
    syncNotificationRef.current = syncNotification;
    const syncErrorRef = useRef<typeof syncError>();
    syncErrorRef.current = syncError;

    // Tracks whether onMessage actually received the BIMQ auth response (as opposed to the user
    // closing the popup themselves before completing authentication). verifyPopup relies on this
    // to tell "the user cancelled" apart from "we closed the popup ourselves after a successful login".
    const authCompletedRef = useRef<boolean>(false);

    // TODO: Pass language/region through context and url
    const elementService = new ElementDatabaseService("int", "int-fr");
    const localeService = new LocaleService("int", "en");
    const syncService = new BimqSynchronisationService();

    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    useEffect(() => {
        Promise.all([
            elementService.get().then(setElements),
            localeService.getDisciplines("wip").then(setDisciplines),
            LocaleService.get().then(parseLanguages),
            syncService.getState().then(handleSyncState),
        ]).then(() => setLoading(false));

        return () => {
            syncService.clean();
            popupRef.current?.close();
            clearTimeout(syncStateTimerRef.current);
            clearTimeout(popupTimerRef.current);
            clearTimeout(notificationTimerRef.current);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!syncNotification.open) return;

        notificationTimerRef.current = setTimeout(() => hideNotification(), 3000);
    }, [syncNotification]);

    const handleCapture = ({ target }: ChangeEvent<HTMLInputElement>) => {
        if (!target.files || target.files.length === 0) return;
        setSelectedFile(target.files[0]);
    };

    const parseLanguages = (regions: Locale[]) => {
        const languages: Language[] = [];
        for (let i = 0; i < regions.length; i++) {
            const region = regions[i];

            for (let j = 0; j < region.languages.length; j++) {
                const language = region.languages[j];
                if (languages.findIndex((x) => x.label === language.label) > -1) continue;

                const split = language.slug.split("-");
                languages.push({ ...language, slug: `int-${split[1]}`, regionLabel: "International" });
            }
        }

        setLanguages(languages.sort((a, b) => a.label.localeCompare(b.label)));
    };

    // Schedules the next state check. Kept separate from handleSyncState so onMessage can start
    // polling as soon as it knows a sync was actually queued, instead of waiting on verifyPopup.
    const pollSyncState = () => {
        syncStateTimerRef.current = setTimeout(() => {
            syncService.getState().then(handleSyncState);
        }, 5000);
    };

    const handleSyncState = (state: SynchronisationStateResponse) => {
        if (state.status === SynchronisationState.Running) {
            setSyncInProgress(true);
            pollSyncState();

            return;
        }

        // Use the ref (not the syncInProgress state variable) since this function can be invoked from
        // callbacks registered on an earlier render (e.g. the recursive pollSyncState timeout), whose
        // closure would otherwise see a stale value of syncInProgress.
        if (syncInProgressRef.current) {
            switch (state.status) {
                case SynchronisationState.CompletedOk:
                    displayNotification("Synchronisation process finished...", "success");
                    break;

                case SynchronisationState.CompletedWithErrors:
                    displayNotification("Synchronisation process finished with errors...", "error");
                    break;
                default:
                    break;
            }
        }

        // Unlike the transient notification above, this reflects the backend's actual last-known outcome
        // regardless of whether this session triggered or watched it - so it's visible on a fresh page
        // load too, not just to whoever was watching the polling when it happened.
        setSyncError({
            open: state.status === SynchronisationState.CompletedWithErrors,
            message: state.message,
        });

        setLastSyncDate(parseJSON(state.lastSyncDate).toLocaleString("nl-be"));
        setSyncInProgress(false);
    };

    const onSync = () => {
        setOpen(true);

        setIgnoreChanges(true);
        setIgnoreFootnotes(true);
        setIgnoreVisibility(true);
    };

    const onCancel = () => setOpen(false);

    const onConfirmSync = async () => {
        setOpen(false);

        setSyncInProgress(true);
        authCompletedRef.current = false;

        try {
            popupRef.current = await syncService.authorize(onMessage);
        } catch {
            displayNotification("Failed to open the BIMQ authentication popup", "error");
            setSyncInProgress(false);
            return;
        }

        if (!popupRef.current) {
            displayNotification("Failed to open the BIMQ authentication popup", "error");
            setSyncInProgress(false);
            return;
        }

        popupTimerRef.current = setInterval(verifyPopup, 1000);
    };

    const verifyPopup = () => {
        if (!popupRef.current || !popupRef.current.closed) return;

        clearInterval(popupTimerRef.current);

        // If we never received the auth response, the user closed the popup themselves (cancelled),
        // so there's nothing to wait for. If we did receive it, onMessage already owns syncInProgress
        // from here on - checking state here as well would race the authenticate/synchronize calls it
        // kicks off, which could read a stale (not-yet-Running) status and re-enable the button early.
        if (!authCompletedRef.current) setSyncInProgress(false);
    };

    const onMessage = async (message: MessageEvent) => {
        try {
            const { data } = message;
            if (data.type !== "BIMQ-AUTH-RESPONSE") return;
            const { code } = data.payload;

            authCompletedRef.current = true;
            popupRef.current?.close();
            window.removeEventListener("message", onMessage);

            if (!code) {
                displayNotification("BIMQ Authentication failed", "error");
                setSyncInProgress(false);
                return;
            }

            const success = await syncService.authenticate(code);
            setSyncInProgress(success);

            if (!success) {
                displayNotification("Failed to start synchronisation process...", "error");
                return;
            }

            await syncService.synchronize(ignoreChanges, ignoreFootnotes, ignoreVisibility, selectedFile);
            displayNotification("Synchronisation process started...", "success");

            // The backend flips its status to Running asynchronously in a background task, so it may not
            // have happened yet even though this call already resolved. Start polling now rather than
            // relying on it - syncInProgress is already true, and the next getState() call will simply
            // keep it true (and re-schedule) until the backend actually reports completion.
            pollSyncState();
        } catch {
            console.warn("Error while processing synchronisation message");
            setSyncInProgress(false);
        }
    };

    const displayNotification = (message: string, type: AlertColor) =>
        setSyncNotification({ open: true, message, type });

    const hideNotification = () => setSyncNotification({ open: false, type: "success" });

    const dismissSyncError = () => setSyncError((prev) => ({ ...prev, open: false }));

    const renderDisciplines = (element: DatabaseElement) => {
        if (!element.disciplines || element.disciplines.length === 0)
            return (
                <Typography color={"text.secondary"} variant={"subtitle2"}>
                    -
                </Typography>
            );

        const output: string[] = [];
        for (let i = 0; i < element.disciplines.length; i++) {
            const disciplineId = element.disciplines[i];
            const discipline = disciplines.find((x) => x.id === disciplineId);

            if (discipline) {
                output.push(discipline.code);
            }
        }

        if (output.length > 0)
            return (
                <Typography color={"text.secondary"} variant={"subtitle2"}>
                    {output.join(", ")}
                </Typography>
            );

        return <Typography>-</Typography>;
    };

    const renderStatus = (status?: string) => {
        if (!status) {
            return (
                <Typography color={"text.secondary"} variant={"subtitle2"}>
                    -
                </Typography>
            );
        }

        let statusColor = theme.palette.success;
        if (status === "Draft") statusColor = theme.palette.warning;
        else if (status === "Old") statusColor = theme.palette.info;

        return (
            <Typography
                variant={"caption"}
                fontWeight={700}
                sx={{
                    bgcolor: alpha(statusColor.light, 0.1),
                    color: statusColor.dark,
                    paddingX: 1.5,
                    paddingY: 0.5,
                    borderRadius: 4,
                    display: "inline",
                }}
            >
                {status}
            </Typography>
        );
    };

    const renderLanguageDetails = (element: DatabaseElement, language: Language) => {
        const elementLanguageDetails = element.details?.find((x) => x.languageCode === language.slug);
        const elementLanguageName = element.objectNames.find((x) => x.item1 === language.slug)?.item2 || "-";

        let creationDate, author, modifiedDate, modifiedAuthor, /* syncDate, */ status;
        if (elementLanguageDetails) {
            creationDate = parseJSON(elementLanguageDetails.creationDate).toLocaleString("nl-be");
            modifiedDate = parseJSON(elementLanguageDetails.modificationDate).toLocaleString("nl-be");
            // syncDate = "-";
            author = elementLanguageDetails.author || "-";
            modifiedAuthor = elementLanguageDetails.modificationAuthor || "-";
            status = elementLanguageDetails.status;
        } else {
            creationDate = "-";
            modifiedDate = "-";
            // syncDate = "-";
            author = "-";
            modifiedAuthor = "-";
        }

        return (
            <>
                <TableCell>
                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                        {language.label}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                        {elementLanguageName}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                        {creationDate}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                        {author}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                        {modifiedDate}
                    </Typography>
                </TableCell>
                {/* <TableCell>
                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                        {syncDate}
                    </Typography>
                </TableCell> */}
                <TableCell>
                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                        {modifiedAuthor}
                    </Typography>
                </TableCell>
                <TableCell>{renderStatus(status)}</TableCell>
                <TableCell>
                    <Typography
                        color={"primary"}
                        variant={"subtitle2"}
                        fontWeight={700}
                        component={Link}
                        to={`${element.id}/${language.slug}`}
                        target={openInNewTab ? "_blank" : "_self"}
                    >
                        Edit
                    </Typography>
                </TableCell>
            </>
        );
    };

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbs} />

            <Divider />

            <Grid container spacing={2} p={{ xs: 2, md: 4 }}>
                <Box sx={{ width: "100%", ml: 2 }}>
                    <Collapse in={syncErrorRef.current?.open ?? false}>
                        <Alert
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => dismissSyncError()}
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </IconButton>
                            }
                            sx={{ mb: 2 }}
                            severity="error"
                        >
                            <Typography variant="body2" fontWeight={700}>
                                Last synchronisation resulted in an error
                            </Typography>
                            {syncErrorRef.current?.message && (
                                <Typography
                                    variant="caption"
                                    component="pre"
                                    sx={{
                                        whiteSpace: "pre-wrap",
                                        maxHeight: 200,
                                        overflow: "auto",
                                        m: 0,
                                        fontFamily: "inherit",
                                    }}
                                >
                                    {syncErrorRef.current.message}
                                </Typography>
                            )}
                        </Alert>
                    </Collapse>
                </Box>

                <Box sx={{ width: "100%", ml: 2 }}>
                    <Collapse in={syncNotificationRef.current.open}>
                        <Alert
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => hideNotification()}
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </IconButton>
                            }
                            sx={{ mb: 2 }}
                            severity={syncNotificationRef.current.type}
                        >
                            {syncNotificationRef.current.message}
                        </Alert>
                    </Collapse>
                </Box>

                <Grid size={{ xs: 12, md: 12 }} sx={{ display: "flex", flexDirection: "column" }}>
                    <Box sx={{ display: "flex", ml: "auto" }}>
                        <Box sx={{ ml: 2, position: "relative" }}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowPathIcon className="h-5 w-5" />}
                                sx={{ ml: "auto" }}
                                onClick={onSync}
                                disabled={syncInProgress}
                            >
                                Synchronize data from BIMQ and update all tables
                            </Button>
                            {syncInProgress && (
                                <CircularProgress
                                    size={24}
                                    sx={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        marginTop: "-12px",
                                        marginLeft: "-12px",
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ ml: "auto", mt: 2, alignSelf: "center" }}>
                        <Typography
                            color={"text.secondary"}
                            variant={"caption"}
                            fontWeight={700}
                            sx={{ textTransform: "uppercase", textAlign: "right" }}
                        >
                            Last successful synchronisation: {lastSyncDate || "-"}
                        </Typography>
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 12 }} sx={{ display: "flex", alignItems: "center" }}>
                    <FormGroup sx={{ ml: "auto" }}>
                        <FormControlLabel
                            control={
                                <Checkbox checked={openInNewTab} onChange={(e) => setOpenInNewTab(e.target.checked)} />
                            }
                            sx={{ mr: 0 }}
                            label={<Typography variant="body2">Edit in new tab</Typography>}
                        />
                    </FormGroup>
                </Grid>

                <Grid size={{ xs: 12, md: 12 }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 750 }} aria-label="simple table">
                            <TableHead sx={{ bgcolor: "alternate.main" }}>
                                <TableRow>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Element
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Discipline
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Language
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Name (Locale)
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Created
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Author
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Modified
                                        </Typography>
                                    </TableCell>
                                    {/* <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Synchronised
                                        </Typography>
                                    </TableCell> */}
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Modification Author
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Status
                                        </Typography>
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={9}
                                            sx={{
                                                height: 400,
                                                alignItems: "center",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", placeContent: "center" }}>
                                                <CircularProgress />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    elements.map((item, i) => {
                                        return (
                                            <Fragment key={i}>
                                                <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                                    <TableCell
                                                        component="th"
                                                        scope="row"
                                                        rowSpan={4}
                                                        sx={{ verticalAlign: "top" }}
                                                    >
                                                        <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                            {item.name} ({item.code})
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell rowSpan={4} sx={{ verticalAlign: "top" }}>
                                                        {renderDisciplines(item)}
                                                    </TableCell>
                                                    {renderLanguageDetails(item, languages[0])}
                                                </TableRow>
                                                {languages.slice(1).map((language, index) => {
                                                    return (
                                                        <TableRow key={index}>
                                                            {renderLanguageDetails(item, language)}
                                                        </TableRow>
                                                    );
                                                })}
                                            </Fragment>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Dialog open={open} onClose={onCancel} fullWidth maxWidth={"sm"} fullScreen={fullScreen}>
                        <DialogTitle>Update Information Tables</DialogTitle>
                        <DialogContent dividers>
                            <DialogContentText>
                                Are you sure you wish to update the content of the GID - Information tables with latest
                                synchronised BIMQ data?
                            </DialogContentText>

                            <Box sx={{ display: "flex" }}>
                                <FormControl sx={{ m: 3 }} component="fieldset" variant="standard">
                                    <FormLabel component="legend">Data retention</FormLabel>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={ignoreChanges}
                                                    onChange={(e) => setIgnoreChanges(e.target.checked)}
                                                    name="changes"
                                                />
                                            }
                                            label="Retain manually modified values"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={ignoreFootnotes}
                                                    onChange={(e) => setIgnoreFootnotes(e.target.checked)}
                                                    name="footnotes"
                                                />
                                            }
                                            label="Retain footnotes"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={ignoreVisibility}
                                                    onChange={(e) => setIgnoreVisibility(e.target.checked)}
                                                    name="visibility"
                                                />
                                            }
                                            label="Retain modified visibility"
                                        />
                                    </FormGroup>
                                </FormControl>
                            </Box>
                        </DialogContent>
                        <DialogContent dividers>
                            <DialogContentText>
                                Select the property overrides document to be used for the update. If no document is
                                specified the previously loaded document will be used.
                            </DialogContentText>

                            <Box sx={{ display: "flex" }}>
                                <FormControl sx={{ m: 3 }} component="fieldset" variant="standard">
                                    <FormLabel component="legend">Properties Override</FormLabel>
                                    <FormGroup sx={{ display: "flex", alignItems: "center", flexDirection: "row" }}>
                                        <input
                                            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                            id="propertiesOverride"
                                            type="file"
                                            onChange={handleCapture}
                                            style={{ display: "none" }}
                                        />
                                        <label htmlFor="propertiesOverride">
                                            <IconButton
                                                color="primary"
                                                aria-label="upload property overrides"
                                                component="span"
                                            >
                                                <AttachFileIcon />
                                            </IconButton>
                                        </label>
                                        <label style={{ marginLeft: 8 }} htmlFor="propertiesOverride">
                                            {selectedFile ? (
                                                <Typography variant="caption">{selectedFile.name}</Typography>
                                            ) : (
                                                <Typography variant="caption">
                                                    No properties override file selected.
                                                </Typography>
                                            )}
                                        </label>
                                    </FormGroup>
                                </FormControl>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={onCancel}>Cancel</Button>
                            <Button variant="contained" onClick={onConfirmSync}>
                                Update
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Overview;
